import { createStandaloneToast } from '@chakra-ui/react';

import customTheme from '../../../chakra';
import config, { isProd } from '../../../config';
import { CookieKeys, getCookie } from '../../../hooks/useCookie';
import { resetAuth } from '../../../utils/auth';
import { createErrorToast } from '../../../utils/toast';

const { toast } = createStandaloneToast({ theme: customTheme });
const MARKETPLACE_UNAUTHORIZED_TOAST_ID = 'marketplace-unauthorized';

export type RequestCredentials = 'omit' | 'same-origin' | 'include';
type RequestHeaders = Headers | [string, string][] | Record<string, string>;

export type RequestConfig<TData = unknown> = {
  baseURL?: string;
  url?: string;
  method?: 'GET' | 'PUT' | 'PATCH' | 'POST' | 'DELETE' | 'OPTIONS' | 'HEAD';
  params?: Record<string, unknown>;
  data?: TData | FormData;
  responseType?: 'arraybuffer' | 'blob' | 'document' | 'json' | 'text' | 'stream';
  signal?: AbortSignal;
  headers?: RequestHeaders;
  credentials?: RequestCredentials;
};

export type ResponseConfig<TData = unknown> = TData;

export type ResponseErrorConfig<TError = unknown> = TError;

/* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars */
export type Client = <TResponseData, _TError = unknown, TRequestData = unknown>(
  requestConfig: RequestConfig<TRequestData>,
) => Promise<ResponseConfig<TResponseData>>;
/* eslint-enable @typescript-eslint/no-unused-vars, no-unused-vars */

const toHeaders = (headers: RequestHeaders | undefined): Headers => new Headers(headers);

const parseResponseBody = async <T>(response: Response): Promise<T | string | null> => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as T;
  } catch {
    return text;
  }
};

const appendSearchParams = (url: URL, params: Record<string, unknown> | undefined) => {
  if (!params) return;
  Object.entries(params).forEach(([key, value]) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v != null) url.searchParams.append(key, String(v));
      });
      return;
    }
    url.searchParams.append(key, String(value));
  });
};

const client: Client = async (requestConfig) => {
  const headers = toHeaders(requestConfig.headers);
  const baseUrl = requestConfig.baseURL ?? config.marketplaceApiUrl;
  const url = new URL(requestConfig.url ?? '', baseUrl);
  appendSearchParams(url, requestConfig.params);

  const accessToken = getCookie<string>(CookieKeys.ACCESS_TOKEN);
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  const body =
    requestConfig.data instanceof FormData
      ? requestConfig.data
      : requestConfig.data != null
      ? JSON.stringify(requestConfig.data)
      : undefined;

  if (body && !(requestConfig.data instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(url.toString(), {
    credentials: requestConfig.credentials ?? 'same-origin',
    method: requestConfig.method,
    body,
    signal: requestConfig.signal,
    headers,
  });

  const data = await parseResponseBody<unknown>(response);

  if (!response.ok) {
    if (response.status === 401) {
      if (isProd) {
        resetAuth({ removeGuid: true, removeAccessToken: true });
      } else if (!toast.isActive(MARKETPLACE_UNAUTHORIZED_TOAST_ID)) {
        toast({
          ...createErrorToast({
            errorMessage: 'Marketplace request was unauthorized.',
            logError: false,
          }),
          id: MARKETPLACE_UNAUTHORIZED_TOAST_ID,
        });
      }
    }
    throw Object.assign(new Error(`HTTP ${response.status}`), {
      status: response.status,
      body: data,
    });
  }

  return data as never;
};

export default client;
